import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import {
  MdAdd,
  MdClose,
  MdContentCopy,
  MdDelete,
  MdEdit,
  MdEmail,
  MdPhone,
  MdRefresh,
  MdSearch,
} from 'react-icons/md';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const STATUS_META = {
  active: {
    label: 'Active',
    badgeClass: 'bg-green-50 text-green-700 border-green-200',
    dotClass: 'bg-green-500',
  },
  'not response': {
    label: 'Not responding',
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
    dotClass: 'bg-amber-500',
  },
  leaved: {
    label: 'Leaved',
    badgeClass: 'bg-slate-100 text-slate-600 border-slate-200',
    dotClass: 'bg-slate-400',
  },
};

const STATUS_RADIO_SELECTED = {
  active: 'border-green-400 bg-green-50 text-green-700',
  'not response': 'border-amber-400 bg-amber-50 text-amber-700',
  leaved: 'border-slate-400 bg-slate-100 text-slate-700',
};

const STATUS_ACCENT = {
  active: '#16a34a',
  'not response': '#d97706',
  leaved: '#475569',
};

const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label }));
const PHONE_LABELS = ['Primary', 'Alternate', 'Emergency'];

const EMPTY_FORM = {
  name: '',
  position: '',
  officialEmail: '',
  contactNumber1: '',
  contactNumber2: '',
  contactNumber3: '',
  description: '',
  responsibleDepartment: '',
  status: 'active',
};

const createEmptyForm = () => ({ ...EMPTY_FORM });

const sortContacts = (list) => {
  return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

const safeLower = (value) => String(value || '').toLowerCase();

const getInitials = (name) => {
  return (name || 'N A')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

const formatTimestamp = (date) => {
  if (!date) return 'Not synced yet';
  return date.toLocaleString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [copiedValue, setCopiedValue] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [formData, setFormData] = useState(() => createEmptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [selectedContact, setSelectedContact] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axiosInstance.get('/admin/contact-persons');
      const list = Array.isArray(resp?.data?.contacts) ? resp.data.contacts : [];
      setContacts(sortContacts(list));
      setLastSyncedAt(new Date());
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to load contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const stats = useMemo(() => {
    const total = contacts.length;
    const active = contacts.filter((c) => c.status === 'active').length;
    const notResponding = contacts.filter((c) => c.status === 'not response').length;
    const leaved = contacts.filter((c) => c.status === 'leaved').length;
    const departments = new Set(contacts.map((c) => c.responsibleDepartment).filter(Boolean)).size;
    return { total, active, notResponding, leaved, departments };
  }, [contacts]);

  const departmentOptions = useMemo(() => {
    return Array.from(new Set(contacts.map((c) => c.responsibleDepartment).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [contacts]);

  const filteredContacts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return contacts.filter((contact) => {
      const matchesStatus = statusFilter === 'all' || contact.status === statusFilter;
      const matchesDepartment =
        departmentFilter === 'all' || (contact.responsibleDepartment || '') === departmentFilter;
      const valuesToSearch = [
        contact.name,
        contact.position,
        contact.responsibleDepartment,
        contact.officialEmail,
        contact.description,
        contact.contactNumber1,
        contact.contactNumber2,
        contact.contactNumber3,
      ]
        .filter(Boolean)
        .map((value) => safeLower(value));
      const matchesSearch = !term || valuesToSearch.some((value) => value.includes(term));
      return matchesStatus && matchesDepartment && matchesSearch;
    }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [contacts, departmentFilter, searchTerm, statusFilter]);

  const handleCopy = async (value) => {
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        setCopiedValue(value);
        setTimeout(() => setCopiedValue(''), 1600);
      }
    } catch (err) {
      console.warn('Clipboard copy failed', err);
    }
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormData(createEmptyForm());
    setFormErrors({});
    setSelectedContact(null);
    setModalOpen(true);
  };

  const openEditModal = (contact) => {
    setModalMode('edit');
    setSelectedContact(contact);
    setFormData({
      name: contact.name || '',
      position: contact.position || '',
      officialEmail: contact.officialEmail || '',
      contactNumber1: contact.contactNumber1 || '',
      contactNumber2: contact.contactNumber2 || '',
      contactNumber3: contact.contactNumber3 || '',
      description: contact.description || '',
      responsibleDepartment: contact.responsibleDepartment || '',
      status: contact.status || 'active',
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedContact(null);
    setFormData(createEmptyForm());
    setFormErrors({});
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setFormErrors({});
    try {
      const payload = { ...formData };
      let response;
      if (modalMode === 'edit' && selectedContact) {
        response = await axiosInstance.put(`/admin/contact-persons/${selectedContact.id}`, payload);
      } else {
        response = await axiosInstance.post('/admin/contact-persons', payload);
      }
      const saved = response?.data?.contact;
      if (saved) {
        setContacts((prev) => {
          const updated = modalMode === 'edit'
            ? prev.map((item) => (item.id === saved.id ? saved : item))
            : [saved, ...prev];
          return sortContacts(updated);
        });
        setLastSyncedAt(new Date());
      }
      closeModal();
    } catch (err) {
      if (err?.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      } else {
        setError(err?.response?.data?.message || 'Failed to save contact');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (contact) => {
    if (!contact?.id) return;
    const result = await Swal.fire({
      title: 'Remove contact?',
      text: `This will permanently remove ${contact.name || 'this contact'}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`/admin/contact-persons/${contact.id}`);
      setContacts((prev) => prev.filter((item) => item.id !== contact.id));
      setLastSyncedAt(new Date());
      Swal.fire({
        title: 'Deleted',
        text: 'Contact removed successfully.',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire('Failed', err?.response?.data?.message || 'Could not delete contact.', 'error');
    }
  };

  const handleStatusChange = async (contact, nextStatus) => {
    if (!contact?.id || contact.status === nextStatus) return;
    setStatusUpdatingId(contact.id);
    try {
      const resp = await axiosInstance.patch(`/admin/contact-persons/${contact.id}/status`, {
        status: nextStatus,
      });
      const updated = resp?.data?.contact;
      setContacts((prev) => {
        if (!updated) {
          return prev.map((item) => (item.id === contact.id ? { ...item, status: nextStatus } : item));
        }
        return prev.map((item) => (item.id === contact.id ? updated : item));
      });
      setLastSyncedAt(new Date());
    } catch (err) {
      Swal.fire('Failed', err?.response?.data?.message || 'Could not update status.', 'error');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const renderSkeletons = () => {
    return Array.from({ length: 6 }).map((_, index) => (
      <div key={`contact-skeleton-${index}`} className="animate-pulse rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-gray-200" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/3 rounded bg-gray-200" />
            <div className="h-3 w-1/4 rounded bg-gray-100" />
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <div className="h-3 w-1/2 rounded bg-gray-100" />
          <div className="h-3 w-3/4 rounded bg-gray-100" />
        </div>
      </div>
    ));
  };

  return (
    <DashboardLayout title="Contacts">
      <div className="mx-auto max-w-7xl p-4 md:p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Key Contact Persons</h1>
            <p className="text-sm text-gray-500">Central directory for admins to manage quick escalation contacts.</p>
            <p className="mt-1 text-xs text-gray-400">{formatTimestamp(lastSyncedAt)}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadContacts}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <MdRefresh className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
            >
              <MdAdd />
              New Contact
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <div className="flex items-start justify-between gap-4">
              <span>{error}</span>
              <button
                type="button"
                onClick={loadContacts}
                className="rounded-md border border-red-200 px-3 py-1 text-xs font-semibold hover:bg-red-100"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">Total contacts</p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{stats.total}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">Active</p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{stats.active}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">Not responding</p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{stats.notResponding}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">Departments covered</p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{stats.departments}</p>
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="flex flex-1 items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
              <MdSearch className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, number, email, or notes"
                className="w-full border-none bg-transparent text-sm text-gray-700 outline-none"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
              >
                <option value="all">All statuses</option>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
              >
                <option value="all">All departments</option>
                {departmentOptions.map((department) => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3 items-start">
          {loading && renderSkeletons()}
          {!loading && filteredContacts.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center">
              <p className="text-base font-semibold text-gray-700">No contacts match your filters</p>
              <p className="mt-2 text-sm text-gray-500">Try adjusting the filters or add a new contact.</p>
            </div>
          )}
          {!loading &&
            filteredContacts.map((contact) => {
              const meta = STATUS_META[contact.status] || STATUS_META.active;
              const phoneNumbers = [
                contact.contactNumber1,
                contact.contactNumber2,
                contact.contactNumber3,
              ].filter(Boolean);
              return (
                <div key={contact.id} className="rounded-lg border border-gray-300 bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                  <div className="flex flex-col">
                    <div className="border-b border-gray-200 bg-gray-50 px-5 py-4">
                      <div className="flex gap-4">
                        <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-md border-2 border-gray-200 bg-white text-lg font-bold text-gray-700">
                          {getInitials(contact.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">Primary Contact</p>
                          <h3 className="text-lg font-semibold text-gray-900 leading-tight mb-1">{contact.name || 'Unnamed contact'}</h3>
                          {contact.position && (
                            <p className="text-sm text-gray-600 mb-2">{contact.position}</p>
                          )}
                          <div className="flex flex-wrap gap-2 items-center">
                            {contact.responsibleDepartment && (
                              <span className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-0.5 text-xs font-medium text-gray-700">
                                {contact.responsibleDepartment}
                              </span>
                            )}
                            <span className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs font-medium ${meta.badgeClass}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${meta.dotClass}`} />
                              {meta.label}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center gap-2">
                        <label className="text-xs font-medium text-gray-600 mr-2">Change Status:</label>
                        <select
                          value={contact.status}
                          onChange={(e) => handleStatusChange(contact, e.target.value)}
                          disabled={statusUpdatingId === contact.id}
                          className="flex-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-gray-400 focus:outline-none"
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={() => openEditModal(contact)}
                            className="rounded border border-blue-300 bg-white p-1.5 text-blue-600 hover:bg-blue-50 transition-colors"
                            title="Edit contact"
                          >
                            <MdEdit className="text-base" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(contact)}
                            className="rounded border border-red-300 bg-white p-1.5 text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete contact"
                          >
                            <MdDelete className="text-base" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="px-5 py-4 space-y-3">
                      {contact.officialEmail && (
                        <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-100">
                          <div className="flex flex-1 items-center gap-3">
                            <MdEmail className="text-gray-400 text-lg flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Official Email</p>
                              <a href={`mailto:${contact.officialEmail}`} className="text-sm text-gray-900 hover:text-blue-600 block break-all">
                                {contact.officialEmail}
                              </a>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCopy(contact.officialEmail)}
                            className="flex-shrink-0 inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <MdContentCopy className="text-sm" />
                            {copiedValue === contact.officialEmail ? 'Copied' : 'Copy'}
                          </button>
                        </div>
                      )}
                      {phoneNumbers.map((phone, index) => (
                        <div key={`${contact.id}-phone-${phone}`} className="flex items-center justify-between gap-3 py-2 border-b border-gray-100">
                          <div className="flex flex-1 items-center gap-3">
                            <MdPhone className="text-gray-400 text-lg flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
                                {PHONE_LABELS[index] || 'Phone'}
                              </p>
                              <a href={`tel:${phone}`} className="text-sm text-gray-900 hover:text-blue-600 block break-words">
                                {phone}
                              </a>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCopy(phone)}
                            className="flex-shrink-0 inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <MdContentCopy className="text-sm" />
                            {copiedValue === phone ? 'Copied' : 'Copy'}
                          </button>
                        </div>
                      ))}
                      {contact.description && (
                        <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-3">
                          <p className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">Description / Notes</p>
                          <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed break-words">{contact.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {modalMode === 'edit' ? 'Edit contact' : 'Add contact'}
                </h2>
                <p className="text-sm text-gray-500">Fill in the details and hit save to update the directory.</p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Full name"
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.name[0]}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Position</label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="e.g. Senior Coordinator"
                  />
                  {formErrors.position && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.position[0]}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Responsible department</label>
                  <input
                    type="text"
                    name="responsibleDepartment"
                    value={formData.responsibleDepartment}
                    onChange={handleInputChange}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="e.g. Exam Board"
                  />
                  {formErrors.responsible_department && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.responsible_department[0]}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Official email</label>
                  <input
                    type="email"
                    name="officialEmail"
                    value={formData.officialEmail}
                    onChange={handleInputChange}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="name@kaaryaalaya.com"
                  />
                  {formErrors.official_email && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.official_email[0]}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm font-medium text-gray-700">Status</p>
                  <div className="mt-2 grid gap-3 sm:grid-cols-3">
                    {STATUS_OPTIONS.map((option) => {
                      const isSelected = formData.status === option.value;
                      const selectedClass = STATUS_RADIO_SELECTED[option.value] || 'border-indigo-400 bg-indigo-50 text-indigo-700';
                      return (
                        <label
                          key={option.value}
                          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                            isSelected ? selectedClass : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="status"
                            value={option.value}
                            checked={isSelected}
                            onChange={handleInputChange}
                            className="text-indigo-600 focus:ring-indigo-500"
                            style={{ accentColor: STATUS_ACCENT[option.value] || '#4f46e5' }}
                          />
                          {option.label}
                        </label>
                      );
                    })}
                  </div>
                  {formErrors.status && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.status[0]}</p>
                  )}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {['contactNumber1', 'contactNumber2', 'contactNumber3'].map((field, index) => (
                  <div key={field}>
                    <label className="text-sm font-medium text-gray-700">{PHONE_LABELS[index]} number</label>
                    <input
                      type="text"
                      name={field}
                      value={formData[field]}
                      onChange={handleInputChange}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      placeholder="+94 70 000 0000"
                    />
                    {formErrors[`contact_number_${index + 1}`] && (
                      <p className="mt-1 text-xs text-red-600">{formErrors[`contact_number_${index + 1}`][0]}</p>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Description / notes</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Escalation notes, best time to reach, responsibilities, etc."
                />
                {formErrors.description && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.description[0]}</p>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {submitting && <MdRefresh className="animate-spin" />}
                  {modalMode === 'edit' ? 'Update contact' : 'Create contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
